import { type Router } from 'vue-router';
import { useUserStore } from '@/store';
import { showLogin, isAuthDisabled } from '@/utils/login';
import {
  consumeContentEditorReturnUrl,
  getContentEditorPreviewUrl
} from '@/utils/contentEditorReturn';

export function createRouterGuards(router: Router) {
  router.beforeEach(async (to, from) => {
    // 编辑器路由要求必须携带绘本 id
    if (to.name === 'edit' && !to.params.id) {
      return { name: 'story' };
    }

    if (to.meta.title) {
      document.title = String(to.meta.title) + ' | AI绘本';
    }

    const userStore = useUserStore();
    const requiresAuth = to.matched.some((r) => r.meta.requiresAuth) && !isAuthDisabled();
    // 设置页内部切换复用已确认的登录状态，避免重复请求用户信息阻塞标签切换；进出设置页仍正常鉴权。
    const canReuseSettingsAuth =
      userStore.isLogin && isSettingsRoute(to.matched) && isSettingsRoute(from.matched);

    if (requiresAuth && !canReuseSettingsAuth) {
      const isLogin = await init();
      if (!isLogin) {
        const previewUrl = getContentEditorPreviewUrl(to.query.previewUrl);
        if (to.name === 'edit') {
          showLogin({
            onClose: () => returnToPreviousPage(previewUrl)
          });
          return false;
        }

        showLogin();
        return false;
      }
    } else if (!userStore.isLogin) {
      try {
        await init();
      } catch (err) {
        console.warn('[guard] 用户初始化失败', err);
      }
    }

    if (to.matched.length === 0) {
      return '/create';
    }

    return true;
  });

  router.onError((error: any) => {
    console.log(error, '路由错误');
  });
}

let userInitPromise: Promise<boolean> | null = null;

const init = async () => {
  const userStore = useUserStore();

  if (userStore.isLogin) return true;

  if (!userInitPromise) {
    userInitPromise = userStore.getUser().finally(() => {
      userInitPromise = null;
    });
  }

  return userInitPromise;
};

const isSettingsRoute = (matched: ReadonlyArray<{ name?: unknown }>) =>
  matched.some((record) => record.name === 'settings');

const returnToPreviousPage = (fallbackUrl = '') => {
  const returnUrl = fallbackUrl || consumeContentEditorReturnUrl();
  if (returnUrl) {
    window.location.replace(returnUrl);
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  window.location.replace('/create');
};
